// dependencies
import React from "react";
import { useIntl } from "react-intl";

// components
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid";
import {
  List,
  ListItem,
} from "@cloudspire/legacy-resources/src/components/List";
import Text from "@cloudspire/legacy-resources/src/components/Text";
import Field from "@cloudspire/legacy-resources/src/components/Field";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import SelectCapacity from "@cloudspire/legacy-resources/src/components/SelectCapacity";
import CheckBoxForWork from "@cloudspire/legacy-resources/src/components/CheckBoxForWork";
import Separator from "@cloudspire/legacy-resources/src/components/Separator";
import CheckBox from "@cloudspire/legacy-resources/src/components/CheckBox2";
import CheckBoxRoomFeature from "@cloudspire/legacy-resources/src/components/CheckBoxRoomFeature";
import Select from "@cloudspire/legacy-resources/src/components/Select2";
import TextInput from "@cloudspire/legacy-resources/src/components/TextInput2";

// containers
import AutoCompleteCity from "@cloudspire/legacy-resources/src/containers/AutoCompleteCity";
import HeadlessDatePickerWithTextInput from "@cloudspire/legacy-resources/src/containers/HeadlessDatePickerWithTextInput";

// constants
import { ROOM_FEATURE_TYPE_INTL_MESSAGES } from "@cloudspire/shared/constants/room/feature";
import { ROOM_RANKING_INTL_MESSAGES } from "@cloudspire/shared/constants/room";
import { HOTEL_CATEGORY_INTL_MESSAGES } from "@cloudspire/shared/constants/hotel";

// contexts
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";

// libraries
import { get } from "@cloudspire/legacy-shared/src/libraries";
import {
  fromDate,
  fromString,
} from "@cloudspire/legacy-resources/src/libraries/EnhancedDate";
import stringUppercaseFirst from "@cloudspire/shared/utils/string/uppercaseFirst";

type PropsType = {
  inline?: boolean;
  parameterList;
  onChange;
};

const RoomListAdditionalFiltersContainer: React.FunctionComponent<PropsType> = (
  props
) => {
  const { inline = false, parameterList, onChange } = props;

  const intl = useIntl();

  const configuration = useConfiguration();

  return (
    <>
      <Row
        breakpointList={{ xs: 800, sm: 1040, md: Infinity }}
        col={{ xs: 1, sm: 1, md: 3 }}
        gutter={0.5}
      >
        {inline && (
          <Col>
            <Field>
              <AutoCompleteCity name="city" onSelectResult={onChange} />
            </Field>
          </Col>
        )}

        {inline && (
          <Col>
            <TextInput
              name="radius"
              id="radius"
              type="number"
              min={1}
              step={1}
              label={intl.formatMessage({
                defaultMessage: "Rayon",
              })}
              placeholder={intl.formatMessage({
                defaultMessage: "Rayon",
              })}
              value={get(parameterList, "radius", "")}
              onChange={onChange}
              onBlur={function (event) {
                const value = parseFloat(event.target.value);
                if (!isNaN(value)) {
                  if (value < 1) {
                    onChange({
                      target: {
                        name: "radius",
                        value: 1,
                      },
                    });
                  }
                }
              }}
            />
          </Col>
        )}

        {inline && (
          <Col>
            <HeadlessDatePickerWithTextInput
              name="checkinCheckout"
              initialDate={fromDate()}
              min={fromDate().startOfDays()}
              start={
                fromString(get(parameterList, "checkin", null), "Y-m-d") || null
              }
              end={
                fromString(get(parameterList, "checkout", null), "Y-m-d") ||
                null
              }
              onSelect={onChange}
            />
          </Col>
        )}

        {inline && (
          <Col>
            <SelectCapacity
              value={get(parameterList, "capacity", "")}
              onChange={onChange}
            />
          </Col>
        )}

        {inline &&
          get(parameterList, "checkin", null) &&
          get(parameterList, "checkout", null) && (
            <Col>
              <TextInput
                name="minAmount"
                id="minAmount"
                type="number"
                min={1}
                step={1}
                label={intl.formatMessage({
                  defaultMessage: "Budget minimum",
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Budget minimum",
                })}
                value={get(parameterList, "minAmount", "")}
                onChange={onChange}
              />
            </Col>
          )}

        {inline &&
          get(parameterList, "checkin", null) &&
          get(parameterList, "checkout", null) && (
            <Col>
              <TextInput
                name="maxAmount"
                id="maxAmount"
                type="number"
                min={1}
                step={1}
                label={intl.formatMessage({
                  defaultMessage: "Budget maximum",
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Budget maximum",
                })}
                value={get(parameterList, "maxAmount", "")}
                onChange={onChange}
              />
            </Col>
          )}

        {inline && (
          <Col>
            <Select
              placeholder={intl.formatMessage({
                defaultMessage: "Type de bien",
              })}
              name="category"
              optionList={configuration.room.categoryNameList.map(
                (roomCategoryName) => ({
                  label: HOTEL_CATEGORY_INTL_MESSAGES.byValue?.[
                    roomCategoryName
                  ]?.label
                    ? stringUppercaseFirst(
                        intl.formatMessage(
                          HOTEL_CATEGORY_INTL_MESSAGES.byValue[roomCategoryName]
                            .label
                        )
                      )
                    : roomCategoryName,
                  value: roomCategoryName,
                })
              )}
              native={true}
              value={get(parameterList, "category", "")}
              onChange={onChange}
            />
          </Col>
        )}

        <Col span={{ xs: 1, sm: 1, md: 1 }}>
          <CheckBox
            id="formIsBookableFront"
            name="isBookableFront"
            label={intl.formatMessage({
              defaultMessage: "Réservable en ligne",
            })}
            value="1"
            checked={get(parameterList, "isBookableFront", false)}
            onChange={onChange}
          />
        </Col>

        <Col span={{ xs: 1, sm: 1, md: 1 }}>
          {/* TODO: modifier si clév ? */}
          <Select
            placeholder={intl.formatMessage({
              defaultMessage: "Classement",
            })}
            name="epi"
            optionList={Object.entries(ROOM_RANKING_INTL_MESSAGES.byCount).map(
              ([value, { label: labelMessageDescriptor }]) => ({
                label: intl.formatMessage(labelMessageDescriptor),
                value: value,
              })
            )}
            native={true}
            value={get(parameterList, "epi", "")}
            onChange={onChange}
          />
        </Col>

        <Col>
          <CheckBox
            id="formComment"
            name="comment"
            label={intl.formatMessage({
              defaultMessage: "Avec avis client",
            })}
            value="1"
            checked={get(parameterList, "comment", false)}
            onChange={onChange}
          />
        </Col>

        <Col>
          <Select
            placeholder={intl.formatMessage({
              defaultMessage: "Animaux autorisés ?",
            })}
            name="pets"
            optionList={[
              {
                label: intl.formatMessage(
                  {
                    defaultMessage: "{choice, select, yes {Oui} other {Non}}",
                  },
                  { choice: "yes" }
                ),
                value: "1",
              },
              {
                label: intl.formatMessage(
                  {
                    defaultMessage: "{choice, select, yes {Oui} other {Non}}",
                  },
                  { choice: "no" }
                ),
                value: "0",
              },
            ]}
            value={get(parameterList, "pets", "")}
            native={true}
            onChange={onChange}
          />
        </Col>

        <Col>
          <TextInput
            name="reference"
            id="formReference"
            label={intl.formatMessage({
              defaultMessage: "Référence",
            })}
            placeholder={intl.formatMessage({
              defaultMessage: "Référence",
            })}
            value={get(parameterList, "reference", "")}
            onChange={onChange}
          />
        </Col>

        <Col>
          <TextInput
            name="beds"
            id="formBeds"
            label={intl.formatMessage({
              defaultMessage: "Nombre minimum de lits",
            })}
            placeholder={intl.formatMessage({
              defaultMessage: "Nombre minimum de lits",
            })}
            value={get(parameterList, "beds", "")}
            onChange={onChange}
          />
        </Col>

        <Col>
          <TextInput
            name="chambers"
            id="formChambers"
            label={intl.formatMessage({
              defaultMessage: "Nombre minimum de chambres",
            })}
            placeholder={intl.formatMessage({
              defaultMessage: "Nombre minimum de chambres",
            })}
            value={get(parameterList, "chambers", "")}
            onChange={onChange}
          />
        </Col>

        <Col>
          <CheckBoxForWork
            name="pros"
            mode="room"
            checked={get(parameterList, "pros", false)}
            value="1"
            onChange={onChange}
          />
        </Col>

        {configuration.room.featureList.length > 0 && (
          <Col span={{ xs: 1, sm: 1, md: 3 }}>
            <Row
              breakpointList={{ xs: 800, sm: 1040, md: Infinity }}
              col={{ xs: 1, sm: 1, md: 1 }}
              gutter={0.5}
            >
              {Object.keys(configuration.room.featureIdListByType)
                .sort((featureType1, featureType2) =>
                  featureType1.localeCompare(featureType2)
                )
                .map((featureType) => {
                  const featureIdList =
                    configuration.room.featureIdListByType[featureType];

                  const featureList = featureIdList.reduce(
                    (featureList, featureId) => {
                      const feature = configuration.room.features[featureId];

                      if (feature.searchable) {
                        featureList.push(feature);
                      }

                      return featureList;
                    },
                    []
                  );

                  if (inline) {
                    return (
                      <Col key={featureType}>
                        <Separator color={"#CFDADD"} />
                        <Stack marginTop={1} />
                        <Text style={{ color: "#000" }}>
                          {ROOM_FEATURE_TYPE_INTL_MESSAGES.byValue?.[
                            featureType
                          ]?.label
                            ? stringUppercaseFirst(
                                intl.formatMessage(
                                  ROOM_FEATURE_TYPE_INTL_MESSAGES.byValue[
                                    featureType
                                  ].label
                                )
                              )
                            : featureType}
                        </Text>
                        <Stack marginTop={1} />
                        <List gutter={0.5}>
                          {featureList.length > 0 &&
                            featureList.map((feature) => (
                              <ListItem key={feature.id}>
                                <CheckBoxRoomFeature
                                  feature={feature}
                                  checked={get(
                                    parameterList,
                                    `features[${feature.id}]`,
                                    false
                                  )}
                                  distanceValue={get(
                                    parameterList,
                                    `featureDistances[${feature.id}]`,
                                    ""
                                  )}
                                  onChange={onChange}
                                />
                              </ListItem>
                            ))}
                        </List>
                      </Col>
                    );
                  } else {
                    return (
                      <Col key={featureType}>
                        <Separator color={"#CFDADD"} />
                        <Stack marginTop={1} />
                        <Row
                          breakpointList={{
                            xs: 800,
                            sm: 1040,
                            md: Infinity,
                          }}
                          col={{ xs: 1, sm: 1, md: 3 }}
                          gutter={0.5}
                        >
                          <Col>
                            <Text style={{ color: "#000" }}>
                              {ROOM_FEATURE_TYPE_INTL_MESSAGES.byValue?.[
                                featureType
                              ]?.label
                                ? stringUppercaseFirst(
                                    intl.formatMessage(
                                      ROOM_FEATURE_TYPE_INTL_MESSAGES.byValue[
                                        featureType
                                      ].label
                                    )
                                  )
                                : featureType}
                            </Text>
                          </Col>

                          <Col span={{ xs: 1, sm: 1, md: 2 }}>
                            <Row
                              breakpointList={{
                                xs: 800,
                                sm: 1040,
                                md: Infinity,
                              }}
                              col={{ xs: 1, sm: 1, md: 3 }}
                              gutter={0.5}
                            >
                              {featureList.map((feature) => {
                                return (
                                  <Col key={feature.id}>
                                    <CheckBoxRoomFeature
                                      feature={feature}
                                      checked={get(
                                        parameterList,
                                        `features[${feature.id}]`,
                                        false
                                      )}
                                      distanceValue={get(
                                        parameterList,
                                        `featureDistances[${feature.id}]`,
                                        ""
                                      )}
                                      onChange={onChange}
                                    />
                                  </Col>
                                );
                              })}
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                    );
                  }
                })}
            </Row>
          </Col>
        )}
      </Row>
    </>
  );
};

export default RoomListAdditionalFiltersContainer;
